import { useMemo } from 'react';

import { Grid2X2Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useUserSettings } from '@/hooks';
import { menuIonStyle } from '@/pages/layouts/getStartedModal/gettingStarted_KeyboardShortcuts/types.ts';
import { colors, Icon } from '@/utils';

import type { Hotkey } from '../hotkeys';
import { LayoutHotkeyNames } from '../hotkeys/types.ts';
import { useResolvedLayoutHotKeys } from './useLayoutHotkeysDefinitions/useResolvedLayoutHotKeys.ts';

type LayoutHotkeyUi = Pick<
  Hotkey,
  'description' | 'menuIconForAction' | 'menuIconBackground' | 'disabled'
>;

export const useLayoutHotkeysDefinitions = (): Record<LayoutHotkeyNames, Hotkey> => {
  const { t } = useTranslation(['landingPage']);
  const { resolvedLayoutHotKeys } = useResolvedLayoutHotKeys();
  const { enablePreviewContent } = useUserSettings();

  return useMemo((): Record<LayoutHotkeyNames, Hotkey> => {
    const layoutHotkeyUi: Record<LayoutHotkeyNames, LayoutHotkeyUi> = {
      [LayoutHotkeyNames.NextLayoutKey]: {
        description: t('landingPage:getStartedWizard.keyboardShortcuts.shortcuts.getNextLayout'),
      },
      [LayoutHotkeyNames.PreviousLayoutKey]: {
        description: t(
          'landingPage:getStartedWizard.keyboardShortcuts.shortcuts.getPreviousLayout'
        ),
      },
      [LayoutHotkeyNames.ToggleEditModeKey]: {
        description: t('landingPage:getStartedWizard.keyboardShortcuts.shortcuts.startEditing'),
        menuIconForAction: <Icon.Pencil size='large' style={menuIonStyle} />,
      },
      [LayoutHotkeyNames.AddWidgetKey]: {
        description: t('landingPage:getStartedWizard.keyboardShortcuts.shortcuts.addNewWidget'),
        menuIconForAction: <Icon.Plus size='large' style={menuIonStyle} />,
      },
      [LayoutHotkeyNames.ReorganizeWidgetsKey]: {
        description: t(
          'landingPage:getStartedWizard.keyboardShortcuts.shortcuts.reorganizeWidgets'
        ),
        menuIconForAction: <Icon.WandSparkles style={menuIonStyle} />,
      },
      [LayoutHotkeyNames.ResetLayoutKey]: {
        description: t('landingPage:getStartedWizard.keyboardShortcuts.shortcuts.undoChanges'),
        menuIconForAction: (
          <Icon.History
            size='large'
            style={{ ...menuIonStyle, width: `0.5rem`, height: `0.5rem` }}
          />
        ),
        menuIconBackground: colors.background.critical.soft['200'],
      },

      [LayoutHotkeyNames.OpenLayoutIndexModalKey]: {
        description: t('landingPage:getStartedWizard.keyboardShortcuts.shortcuts.showLayoutIndex'),
        menuIconForAction: <Grid2X2Check strokeWidth={1} />,
        // menuIconBackground: colors.background.action.soft['200'],
        disabled: !enablePreviewContent,
      },
      [LayoutHotkeyNames.SaveLayoutKey]: {
        description: t('landingPage:getStartedWizard.keyboardShortcuts.shortcuts.saveChanges'),
        menuIconForAction: <Icon.Check size='large' style={menuIonStyle} />,
        menuIconBackground: colors.background.success.soft['300'],
      },
    };

    const result = {} as Record<LayoutHotkeyNames, Hotkey>;
    for (const name of Object.values(LayoutHotkeyNames) as LayoutHotkeyNames[]) {
      result[name] = {
        ...resolvedLayoutHotKeys[name],
        ...layoutHotkeyUi[name],
      };
    }
    return result;
  }, [t, enablePreviewContent, resolvedLayoutHotKeys]);
};
